import type { SlotDto, PublicConfirmResponse, PublicEventInfoResponse } from "@/services/types";

export type BookingState =
  | "EVENT"
  | "SLOTS"
  | "DETAILS"
  | "HELD"
  | "PAYMENT"
  | "CONFIRMED"
  | "CANCELLED"
  | "EXPIRED";

export interface BookingContextData {
  state: BookingState;
  eventInfo: PublicEventInfoResponse | null;
  username: string | null;
  eventTypeSlug: string | null;
  selectedDate: string | null;
  selectedSlot: SlotDto | null;
  details: { name: string; email: string; notes: string; guestEmails: string[] };
  hold: {
    bookingId: string;
    expiresAt: string;
    paymentRequired?: boolean;
    paymentAmountMinor?: number | null;
    paymentCurrency?: string | null;
    paymentProvider?: string | null;
  } | null;
  confirmation: PublicConfirmResponse | null;
  confirmedAt: string | null;
  loading: boolean;
  error: { code: string; message: string } | null;
  attemptIdempotencyKey: string | null;
  attemptSlotId: string | null;
  attemptStartedAt: string | null;
  attemptGuestEmail: string | null;
  attemptGuestName: string | null;
}

export type BookingEvent =
  | { type: "SET_PUBLIC_ROUTE"; username: string; eventTypeSlug: string }
  | { type: "EVENT_LOADED"; eventInfo: PublicEventInfoResponse }
  | { type: "EVENT_LOAD_FAILED"; error: { code: string; message: string } }
  | { type: "GO_TO_SLOTS" }
  | { type: "SELECT_DATE"; date: string }
  | { type: "SELECT_SLOT"; slot: SlotDto }
  | { type: "GO_TO_DETAILS" }
  | { type: "UPDATE_DETAILS"; details: Partial<BookingContextData["details"]> }
  | { type: "SET_ATTEMPT"; idempotencyKey: string; slotId: string; startedAt: string; guestEmail: string; guestName: string }
  | { type: "HOLD_REQUESTED" }
  // `stayOnDetails` is set by the public flow, which confirms in the same action and so must not
  // flash the review step in between. The embed omits it and still lands on HELD, which is its
  // own review-and-confirm screen. State alone cannot tell the two apart — both hold from
  // DETAILS — so the intent travels with the event.
  | { type: "HOLD_SUCCEEDED"; hold: NonNullable<BookingContextData["hold"]>; stayOnDetails?: boolean }
  | { type: "HOLD_FAILED"; error: { code: string; message: string } }
  | { type: "RELEASE_HOLD" }
  | { type: "CONFIRM_REQUESTED" }
  | { type: "CONFIRM_SUCCEEDED"; confirmation: PublicConfirmResponse }
  | { type: "CONFIRM_FAILED"; error: { code: string; message: string } }
  | { type: "EXPIRE" }
  | { type: "CANCEL" }
  | { type: "BACK" }
  | { type: "RESET" }
  | { type: "ERROR_CLEARED" }
  | {
      type: "HYDRATE_FROM_STORAGE";
      payload: {
        state: BookingState;
        selectedDate: string | null;
        selectedSlot: SlotDto | null;
        details: BookingContextData["details"];
        hold: BookingContextData["hold"];
        attemptIdempotencyKey: string | null;
        attemptSlotId: string | null;
        attemptStartedAt: string | null;
        attemptGuestEmail: string | null;
        attemptGuestName: string | null;
      };
    };

const allowed: Record<BookingState, BookingEvent["type"][]> = {
  EVENT: ["SET_PUBLIC_ROUTE", "EVENT_LOADED", "EVENT_LOAD_FAILED", "GO_TO_SLOTS", "SELECT_DATE", "RESET", "ERROR_CLEARED", "HYDRATE_FROM_STORAGE"],
  // SLOTS accepts the hold events because the public page now reserves the slot at Continue,
  // before the guest's details are collected. The embed and group flows still hold from
  // DETAILS, which is why HOLD_SUCCEEDED below branches on the originating state.
  // SET_ATTEMPT belongs here because the hold is now taken from this step: acquireHold records
  // the idempotency attempt before calling out.
  SLOTS: ["SET_PUBLIC_ROUTE", "SELECT_DATE", "SELECT_SLOT", "GO_TO_DETAILS", "SET_ATTEMPT", "HOLD_REQUESTED", "HOLD_SUCCEEDED", "HOLD_FAILED", "RELEASE_HOLD", "BACK", "RESET", "EVENT_LOADED", "EVENT_LOAD_FAILED", "ERROR_CLEARED", "HYDRATE_FROM_STORAGE"],
  // CONFIRM_* are allowed here because the public flow confirms without leaving this step.
  DETAILS: ["SET_PUBLIC_ROUTE", "EVENT_LOADED", "EVENT_LOAD_FAILED", "UPDATE_DETAILS", "SET_ATTEMPT", "HOLD_REQUESTED", "HOLD_SUCCEEDED", "HOLD_FAILED", "RELEASE_HOLD", "CONFIRM_REQUESTED", "CONFIRM_SUCCEEDED", "CONFIRM_FAILED", "EXPIRE", "BACK", "RESET", "ERROR_CLEARED", "HYDRATE_FROM_STORAGE"],
  HELD: ["SET_PUBLIC_ROUTE", "EVENT_LOADED", "EVENT_LOAD_FAILED", "CONFIRM_REQUESTED", "CONFIRM_SUCCEEDED", "CONFIRM_FAILED", "EXPIRE", "CANCEL", "BACK", "RESET", "ERROR_CLEARED", "HYDRATE_FROM_STORAGE"],
  PAYMENT: ["SET_PUBLIC_ROUTE", "EVENT_LOADED", "EVENT_LOAD_FAILED", "CONFIRM_REQUESTED", "CONFIRM_SUCCEEDED", "CONFIRM_FAILED", "EXPIRE", "CANCEL", "BACK", "RESET", "ERROR_CLEARED", "HYDRATE_FROM_STORAGE"],
  CONFIRMED: ["CANCEL", "RESET", "HYDRATE_FROM_STORAGE"],
  CANCELLED: ["RESET", "HYDRATE_FROM_STORAGE"],
  EXPIRED: ["RESET", "BACK", "HYDRATE_FROM_STORAGE"],
};

export const initialContext: BookingContextData = {
  state: "EVENT",
  eventInfo: null,
  username: null,
  eventTypeSlug: null,
  selectedDate: null,
  selectedSlot: null,
  details: { name: "", email: "", notes: "", guestEmails: [] },
  hold: null,
  confirmation: null,
  confirmedAt: null,
  loading: false,
  error: null,
  attemptIdempotencyKey: null,
  attemptSlotId: null,
  attemptStartedAt: null,
  attemptGuestEmail: null,
  attemptGuestName: null,
};

export function reducer(ctx: BookingContextData, ev: BookingEvent): BookingContextData {
  if (!allowed[ctx.state].includes(ev.type)) {
    if (import.meta.env.DEV) {
      console.warn(`[bookingMachine] illegal transition ${ev.type} from ${ctx.state}`);
    }
    return ctx;
  }

  switch (ev.type) {
    case "SET_PUBLIC_ROUTE":
      if (ctx.username !== ev.username || ctx.eventTypeSlug !== ev.eventTypeSlug) {
        return {
          ...initialContext,
          username: ev.username,
          eventTypeSlug: ev.eventTypeSlug,
        };
      }
      return { ...ctx, username: ev.username, eventTypeSlug: ev.eventTypeSlug };
    case "EVENT_LOADED":
      return { ...ctx, eventInfo: ev.eventInfo, error: null };
    case "EVENT_LOAD_FAILED":
      // Drop back to EVENT and clear any rehydrated event/selection. Without this a session
      // restored into SLOTS keeps rendering the booking shell for an event that just failed to
      // load — e.g. a link whose host has deleted their account.
      return {
        ...ctx,
        state: "EVENT",
        eventInfo: null,
        selectedDate: null,
        selectedSlot: null,
        error: ev.error,
        loading: false,
      };
    case "GO_TO_SLOTS":
      return { ...ctx, state: "SLOTS", error: null };
    case "SELECT_DATE":
      return {
        ...ctx,
        selectedDate: ev.date,
        selectedSlot: null,
        // Changing day clears the selection, so any hold on it is equally stale.
        hold: null,
        attemptIdempotencyKey: null,
        attemptSlotId: null,
        attemptStartedAt: null,
        attemptGuestEmail: null,
        attemptGuestName: null,
      };
    case "SELECT_SLOT":
      return {
        ...ctx,
        selectedSlot: ev.slot,
        // Any hold belongs to the slot the guest just moved away from. Dropping it here means
        // a later confirm can never target the old booking; the caller releases it server-side.
        hold: null,
        attemptIdempotencyKey: null,
        attemptSlotId: null,
        attemptStartedAt: null,
        attemptGuestEmail: null,
        attemptGuestName: null,
      };
    case "GO_TO_DETAILS":
      if (!ctx.selectedSlot) return ctx;
      return { ...ctx, state: "DETAILS", error: null };
    case "UPDATE_DETAILS":
      return { ...ctx, details: { ...ctx.details, ...ev.details } };
    case "SET_ATTEMPT":
      return {
        ...ctx,
        attemptIdempotencyKey: ev.idempotencyKey,
        attemptSlotId: ev.slotId,
        attemptStartedAt: ev.startedAt,
        attemptGuestEmail: ev.guestEmail,
        attemptGuestName: ev.guestName,
      };
    case "HOLD_REQUESTED":
      return { ...ctx, loading: true, error: null };
    case "HOLD_SUCCEEDED":
      // Where this lands depends on where it came from, because two flows hold at different
      // points. From SLOTS the guest has only picked a time — the slot is now reserved and
      // they go on to fill in the form, so payment cannot be decided yet. From DETAILS the
      // form is already filled (embed and group flows, and the confirm leg of the public
      // one), which is the original behaviour and must stay untouched.
      if (ctx.state === "SLOTS") {
        return { ...ctx, state: "DETAILS", hold: ev.hold, loading: false, error: null };
      }
      // A paid event has to stop at PAYMENT so the card is taken before anything is committed.
      if (ev.hold?.paymentRequired) {
        return { ...ctx, state: "PAYMENT", hold: ev.hold, loading: false, error: null };
      }
      // Free events on the public flow stay on DETAILS while the confirm call that follows
      // completes, rather than flashing through a review screen the guest never acts on.
      if (ev.stayOnDetails) {
        return { ...ctx, hold: ev.hold, loading: true, error: null };
      }
      return { ...ctx, state: "HELD", hold: ev.hold, loading: false, error: null };
    case "RELEASE_HOLD":
      // The hold is gone server-side (the guest picked a different slot, or we let it go).
      // Clearing it locally keeps a stale bookingId from being confirmed later.
      return { ...ctx, hold: null };
    case "HOLD_FAILED":
      return {
        ...ctx,
        loading: false,
        error: ev.error,
        attemptIdempotencyKey: null,
        attemptSlotId: null,
        attemptStartedAt: null,
        attemptGuestEmail: null,
        attemptGuestName: null,
      };
    case "CONFIRM_REQUESTED":
      return { ...ctx, loading: true, error: null };
    case "CONFIRM_SUCCEEDED":
      return { ...ctx, state: "CONFIRMED", loading: false, confirmedAt: new Date().toISOString(), confirmation: ev.confirmation };
    case "CONFIRM_FAILED":
      return { ...ctx, loading: false, error: ev.error };
    case "EXPIRE":
      return { ...ctx, state: "EXPIRED", hold: null };
    case "CANCEL":
      return { ...ctx, state: "CANCELLED" };
    case "BACK": {
      const back: Record<BookingState, BookingState> = {
        EVENT: "EVENT",
        SLOTS: "EVENT",
        DETAILS: "SLOTS",
        HELD: "DETAILS",
        PAYMENT: "DETAILS",
        CONFIRMED: "CONFIRMED",
        CANCELLED: "CANCELLED",
        EXPIRED: "SLOTS",
      };
      return { ...ctx, state: back[ctx.state], error: null };
    }
    case "ERROR_CLEARED":
      return { ...ctx, error: null };
    case "HYDRATE_FROM_STORAGE":
      return {
        ...ctx,
        state: ev.payload.state,
        selectedDate: ev.payload.selectedDate,
        selectedSlot: ev.payload.selectedSlot,
        details: ev.payload.details,
        hold: ev.payload.hold,
        attemptIdempotencyKey: ev.payload.attemptIdempotencyKey,
        attemptSlotId: ev.payload.attemptSlotId,
        attemptStartedAt: ev.payload.attemptStartedAt,
        attemptGuestEmail: ev.payload.attemptGuestEmail,
        attemptGuestName: ev.payload.attemptGuestName,
        error: null,
        loading: false,
      };
    case "RESET":
      return {
        ...initialContext,
        eventInfo: ctx.eventInfo,
        username: ctx.username,
        eventTypeSlug: ctx.eventTypeSlug,
        state: "SLOTS",
      };
    default:
      return ctx;
  }
}

export const STEP_LABELS = ["Event", "Pick a time", "Your details", "Confirm", "Booked"] as const;

/**
 * The steps a given event actually walks through.
 *
 * <p>A free booking confirms as part of submitting details, so it never lands on "Confirm" —
 * showing that pip would promise a screen the guest never sees, and leave the indicator sitting
 * on step 3 while the booking completes. Paid events do stop to take a card, so they keep it.
 *
 * <p>The embed is unaffected: it defines its own three-step labels locally.
 */
export function stepLabelsFor(paymentRequired: boolean | undefined): readonly string[] {
  if (paymentRequired) return STEP_LABELS;
  return STEP_LABELS.filter((label) => label !== "Confirm");
}

/** Index into {@link stepLabelsFor}'s output, which drops "Confirm" for free events. */
export function stepIndexFor(s: BookingState, paymentRequired: boolean | undefined): number {
  const raw = stepIndex(s);
  if (paymentRequired) return raw;
  // "Booked" moves from 4 to 3 once "Confirm" is dropped. HELD is only transiently reachable
  // here (a restored session, or a refunded payment), so it maps onto the details step.
  if (s === "CONFIRMED") return 3;
  if (s === "HELD" || s === "PAYMENT") return 2;
  return raw;
}

export function stepIndex(s: BookingState): number {
  switch (s) {
    case "EVENT":
      return 0;
    case "SLOTS":
      return 1;
    case "DETAILS":
      return 2;
    case "HELD":
    case "PAYMENT":
      return 3;
    case "CONFIRMED":
      return 4;
    default:
      return 1;
  }
}
